import { useNavigate } from 'react-router';
import {
  Box,
  Center,
  Column,
  DataGrid,
  Pagination,
  Row,
  SortingArrow,
  Text,
  type TSortValue,
} from '~/commonComponents';
import { ICandidateListViewEntity } from '~/domain/PermanentJobs';
import DefaultProfileImage from '~/images/doctor.png';
import { ChevronRight } from '~/images/icons';
import { firePermCandidateListCandidateClicked } from '~/posthogs/events';
import { Colors } from '~/themes/colors';
import { EmptyState } from './EmptyState';
import { InviteColumnCell } from './InviteColumnCell';
import { ProcessStepCell } from './ProcessStepCell';
import { ShortlistCell } from './ShortlistCell';

interface ICandidateListTableV2Props {
  data: ICandidateListViewEntity[];
  isLoading: boolean;
  jobPostingId: string;
  pagination: { currentPage: number; totalPages: number };
  sorting: TSortValue;
  hideInviteColumn?: boolean;
  hideShortlistColumn?: boolean;
  onPageChange: (page: number) => void;
  onSortingChange?: (value: TSortValue | undefined) => void;
}

export const CandidateListTableV2 = ({
  data,
  isLoading,
  jobPostingId,
  sorting,
  onSortingChange,
  pagination,
  onPageChange,
  hideShortlistColumn = false,
  hideInviteColumn = false,
}: ICandidateListTableV2Props) => {
  const navigate = useNavigate();

  const columns = [
    {
      key: 'candidates',
      header: (
        <Text bold color={Colors.primary_500} fontSize={18} marginLeft={86}>
          Candidates
        </Text>
      ),
      width: '1fr',
      render: (item: ICandidateListViewEntity) => {
        const hasPendingCounts = item.pendingApplicationCount > 0;
        const shouldTrimCounts = item.pendingApplicationCount >= 10;

        return (
          <Row alignItems="center" display="flex" gap={20} paddingLeft={18}>
            <Box width={48} height={48} position="relative">
              <Box
                alignItems="center"
                backgroundColor={Colors.primary_100}
                borderRadius="100%"
                height={48}
                justifyContent="center"
                width={48}
              >
                {item.professional.picture ? (
                  <img
                    alt={`${item.professional.name} profile picture`}
                    src={item.professional.picture}
                    height="100%"
                    width="100%"
                    style={{ borderRadius: 100, objectFit: 'cover' }}
                  />
                ) : (
                  <img alt="" src={DefaultProfileImage} height={36} width={36} />
                )}
              </Box>
              {hasPendingCounts && (
                <Center
                  backgroundColor={Colors.error_500}
                  borderRadius="100%"
                  data-testid="pending-badge"
                  height={20}
                  position="absolute"
                  right={-10}
                  top={-10}
                  width={20}
                >
                  <Text
                    bold
                    color={Colors.white}
                    fontSize={14}
                    {...(shouldTrimCounts ? { marginLeft: 2.5 } : {})}
                  >
                    {shouldTrimCounts ? '9+' : item.pendingApplicationCount}
                  </Text>
                </Center>
              )}
            </Box>
            <Column>
              <Text bold fontSize={18} textDecoration="underline">
                {item.professional.name}
              </Text>
              <Text color={Colors.neutral_500}>
                <Text bold color={Colors.neutral_500}>
                  Experience
                </Text>
                : {item.yearsOfExperience} years
              </Text>
              <Text color={Colors.neutral_500}>
                <Text bold color={Colors.neutral_500}>
                  Applied on
                </Text>
                : {item.createdAt}
              </Text>
            </Column>
          </Row>
        );
      },
    },
    {
      key: 'processStep',
      header: (
        <SortingArrow
          name="status"
          sorting={sorting}
          style={{ justifyContent: 'flex-start' }}
          variant="primary"
          onChange={onSortingChange}
        >
          <Text bold color={Colors.primary_500} fontSize={18} marginLeft={12}>
            Process Step
          </Text>
        </SortingArrow>
      ),
      width: '30%',
      render: (item: ICandidateListViewEntity) => <ProcessStepCell status={item.officeStatus} />,
    },
    ...(!hideShortlistColumn
      ? [
          {
            key: 'shortlist',
            header: (
              <SortingArrow
                name="shortlist"
                sorting={sorting}
                variant="primary"
                onChange={onSortingChange}
              >
                <Text bold color={Colors.primary_500} fontSize={18}>
                  Shortlist
                </Text>
              </SortingArrow>
            ),
            width: '15%',
            render: (item: ICandidateListViewEntity) => (
              <ShortlistCell
                candidateId={item.id}
                isShortlisted={item.isShortlisted}
                officeStatus={item.officeStatus}
                jobPostingId={jobPostingId}
              />
            ),
          },
        ]
      : [
          {
            key: 'shortlist',
            header: '',
            width: '15%',
            render: () => null,
          },
        ]),
    ...(!hideInviteColumn
      ? [
          {
            key: 'invite',
            header: (
              <Text bold color={Colors.primary_500} fontSize={18} marginLeft={12}>
                Invite
              </Text>
            ),
            width: '15%',
            render: (item: ICandidateListViewEntity) => (
              <InviteColumnCell
                candidateId={item.id}
                jobPostingId={jobPostingId}
                officeStatus={item.officeStatus}
              />
            ),
          },
        ]
      : [
          {
            key: 'invite',
            header: '',
            width: '15%',
            render: () => null,
          },
        ]),
    {
      key: 'chevron',
      header: '',
      width: '42px',
      render: () => <ChevronRight />,
    },
  ];

  return (
    <DataGrid<ICandidateListViewEntity>
      EmptyComponent={<EmptyState />}
      PaginationComponent={
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={onPageChange}
        />
      }
      columns={columns}
      data={data}
      dataCy={{ rowItem: 'pj-candidate-list-item' }}
      dataTestId={{ rowItem: 'pj-candidate-list-item' }}
      isLoading={isLoading}
      showHeaderSeparator={false}
      showRowSeparator={false}
      getRowClassName={() => 'shadow-xs hover__shadow-sm'}
      getRowStyle={() => ({
        backgroundColor: Colors.white,
        borderRadius: 10,
        marginBottom: 8,
        paddingBlock: 12,
      })}
      keyExtractor={(item) => item.id}
      onRowClick={(item) => {
        firePermCandidateListCandidateClicked({
          postingId: jobPostingId,
          professionalId: item.professional.id,
          candidateStatus: item.officeStatus,
        });

        navigate(`/jobs/${item.onsiteInterviewId}/candidates/${item.professional.id}/overview`);
      }}
    />
  );
};
